import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { http } from "@/lib/api";
import { Lightning, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatApiErrorDetail(detail, error) {
  if (detail == null) {
    // No response body — usually CORS, network failure, or server crash. Tell
    // the user something actionable instead of the generic fallback.
    if (error && !error.response) {
      return "Cannot reach the server. Check your connection or try again in a moment.";
    }
    return "Something went wrong. Please try again.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { user, login, register: doRegister } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from?.pathname || "/live";

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user) {
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await doRegister(email, password, name);
      }
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail, e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#111111] text-white ll-grid-bg" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#0d0d0d] border-r border-[#2a2a2a] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#ff8c00]/5 via-transparent to-[#4682b4]/5 pointer-events-none"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#ff8c00] flex items-center justify-center">
              <Lightning size={26} weight="fill" color="#000" />
            </div>
            <div>
              <div className="font-heading font-black text-2xl tracking-tight leading-none">LOAD LOGIC</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mt-1">Dispatch Operating System</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] mb-3">Control Room Access</div>
            <h1 className="font-heading font-black text-5xl tracking-tight leading-[1.05] mb-6">
              Move heavy.<br />
              Move <span className="text-[#ff8c00]">compliant</span>.<br />
              Move <span className="text-[#4682b4]">smart</span>.
            </h1>
            <p className="text-sm text-[#a1a1aa] max-w-md leading-relaxed">
              Real-time fleet tracking, CDL weight compliance, dispatch boards, and geofenced alerts &mdash; built for dispatchers who never miss a load.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-8 border-t border-[#2a2a2a]">
            <Stat label="Active Fleet" value="12" color="#22c55e" />
            <Stat label="Live Updates" value="~2s" color="#ff8c00" />
            <Stat label="Geofences" value="3+" color="#4682b4" />
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <div className="w-10 h-10 bg-[#ff8c00] flex items-center justify-center">
              <Lightning size={22} weight="fill" color="#000" />
            </div>
            <div className="font-heading font-black text-xl tracking-tight">LOAD LOGIC</div>
          </div>

          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
              {mode === "login" ? "Authenticate" : "Provision Account"}
            </div>
            <h2 className="font-heading font-black text-3xl tracking-tight mt-1">
              {mode === "login" ? "Dispatcher Login" : "Create Account"}
            </h2>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <Field label="Name">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-3 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                  data-testid="name-input"
                  placeholder="D. Reeves"
                />
              </Field>
            )}

            <Field label="Email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-3 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                data-testid="email-input"
              />
            </Field>

            <Field label="Password">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-3 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm font-mono"
                data-testid="password-input"
              />
            </Field>

            {err && (
              <div className="border border-[#ef4444]/40 bg-[#ef4444]/10 text-[#ef4444] text-xs uppercase tracking-wider px-3 py-2.5" data-testid="auth-error">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-xs uppercase tracking-[0.25em] font-black transition-colors"
              data-testid="submit-btn"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <>
                {mode === "login" ? "Authenticate" : "Provision"} <ArrowRight size={14} weight="bold" />
              </>}
            </button>

            <div className="text-center text-xs text-[#71717a] uppercase tracking-wider pt-2">
              {mode === "login" ? "Need an account?" : "Have an account?"}{" "}
              <button
                type="button"
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
                className="text-[#ff8c00] hover:underline font-bold"
                data-testid="toggle-mode"
              >
                {mode === "login" ? "Register" : "Login"}
              </button>
            </div>

            {mode === "login" && (
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={async () => {
                    if (!email) { setErr("Enter your email first"); return; }
                    try {
                      await http.post("/auth/forgot-password", { email });
                      toast.success("If this email exists, a reset link has been sent.");
                    } catch (_e) {
                      toast.error("Could not send reset email");
                    }
                  }}
                  className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] hover:text-[#4682b4]"
                  data-testid="forgot-link"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>

        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">{label}</div>
      <div className="font-mono font-bold text-2xl mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
